import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, viewChildren } from '@angular/core'
import { RouterLink } from '@angular/router'

import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTooltipModule } from '@angular/material/tooltip'

import { Api } from '../../shared/api'
import { Card } from '../../shared/card/card'
import { CardGroup } from '../../shared/card-group/card-group'
import { ExpandCollapseMenu } from '../../shared/expand-collapse-menu/expand-collapse-menu'
import { GroupByMenu, GroupByOption } from '../../shared/group-by-menu/group-by-menu'
import { NewlinesToBrPipe } from '../../shared/newlines-to-br-pipe'
import { Interaction, TimeUnit } from '../interaction-interface'
import { InteractionCardContent } from '../interaction-card-content/interaction-card-content'
import { Interactions } from '../interactions'
import { PageHeaderBar } from '../../shared/page-header-bar/page-header-bar'
import { Relationships } from '../../relationships/relationships'
import { ResponsiveContent } from '../../shared/responsive-content/responsive-content'
import { ResponsiveContentElement } from '../../shared/responsive-content/responsive-content-element'
import { Row } from '../../shared/row/row'
import { SidePanel } from '../../shared/side-panel/side-panel'
import { Topics } from '../../topics/topics'
import { TOPIC_HINT_VERBIAGE } from '../../shared/misc-constants'

@Component({
	selector: 'app-interactions-list',
	imports: [
		/* angular */ RouterLink,
		/* material: */ MatButtonModule, MatChipsModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule,
		/* app */ Card, CardGroup, ExpandCollapseMenu, GroupByMenu, InteractionCardContent, NewlinesToBrPipe, PageHeaderBar, ResponsiveContent, ResponsiveContentElement, Row, SidePanel,
	],
	providers: [Topics],
	templateUrl: './interactions-list.html',
	styleUrl: './interactions-list.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionsList implements OnInit {
	// services
	private readonly api = inject(Api)
	private readonly interactionsService = inject(Interactions)
	private readonly relationshipsService = inject(Relationships)
	private readonly snackBar = inject(MatSnackBar)
	protected readonly topicsService = inject(Topics)

	private readonly cardGroups = viewChildren(CardGroup)

	private readonly interactions = signal<Interaction[]>([])

	// interaction grouping state
	protected readonly groupBy = signal<TimeUnit>('month')
	private readonly groupingResults = computed(() => this.interactionsService.groupBy(this.interactions(), this.groupBy(), this.highlightInteraction))
	protected readonly groupedInteractions = computed(() => this.groupingResults().groups)
	protected readonly highlightedCard = computed(() => {
		const { groupKey, indexInGroup } = this.groupingResults()
		return { groupKey, indexInGroup }
	})

	/** Options for the group-by menu. */
	protected readonly groupByOptions: GroupByOption[] = [
		{ value: 'day', icon: 'today', label: 'Group by Day' },
		{ value: 'week', icon: 'date_range', label: 'Group by Week' },
		{ value: 'month', icon: 'calendar_month', label: 'Group by Month' },
		{ value: 'year', icon: 'calendar_clock', label: 'Group by Year' },
	]

	// misc state
	protected readonly showExpandAllGroupsButton = computed(() => !this.cardGroups().every(group => group.open()))
	protected readonly showCollapseAllGroupsButton = computed(() => !this.cardGroups().every(group => !group.open()))
	protected readonly isLoadingInteractions = signal(true)
	private highlightInteraction = {} as Interaction
	protected readonly TOPIC_HINT_VERBIAGE = TOPIC_HINT_VERBIAGE

	ngOnInit(): void {
		this.loadInteractions()
	}

	/** Fetch interactions from the API and update the loading state. */
	private loadInteractions(): void {
		this.api.getInteractions().subscribe({
			next: interactions => {
				this.interactions.set(interactions)
				this.isLoadingInteractions.set(false)
			},
			error: () => {
				this.isLoadingInteractions.set(false)
				this.snackBar.open('Failed to load interactions.', undefined)
			}
		})
	}

	/** Expand or collapse every interaction group. */
	protected collapseOrExpandAllGroups(open: boolean): void {
		this.cardGroups().forEach(group => group.open.set(open))
	}

	/** Open the add-interaction flow and highlight the newly saved interaction. */
	protected async addInteraction(): Promise<void> {
		const { wasCancelled, interaction, updatedInteractions } = await this.interactionsService.addInteraction(this.interactions())
		if (wasCancelled) return

		this.highlightInteraction = interaction
		this.interactions.set(updatedInteractions)
	}

	/** Open the edit-interaction flow and highlight the saved interaction. */
	protected async editInteraction(editTarget: Interaction): Promise<void> {
		const { wasCancelled, interaction, updatedInteractions } = await this.interactionsService.editInteraction(editTarget, this.interactions())
		if (wasCancelled) return

		this.highlightInteraction = interaction
		this.interactions.set(updatedInteractions)
	}

	/** Delete an interaction from the current list after confirmation. */
	protected deleteInteraction(deleteTarget: Interaction): void {
		this.interactionsService.deleteInteraction(deleteTarget).subscribe(targetDeleted => {
			if (!targetDeleted) return

			const deleteIndex = this.interactions().findIndex(({ _id }) => _id === deleteTarget._id)
			this.interactions.set([
				...this.interactions().slice(0, deleteIndex),
				...this.interactions().slice(deleteIndex + 1)
			])
		})
	}

	/** Open the relationship editor for an interaction owner and refresh the page's interaction data as needed. */
	protected editRelationship(interaction: Interaction): void {
		this.relationshipsService.editRelationship(interaction.idOfRelationship!, this.interactions())
			.subscribe(({ wasCancelled, refreshedInteractionsList, wasNameModified, wereInteractionsModified }) => {
				if (wasCancelled) return
				if (wasNameModified || wereInteractionsModified) this.interactions.set(refreshedInteractionsList)
			})
	}

}

export default InteractionsList
